import React, { useCallback, useState } from 'react';
import { OpportunitiesContext } from './OpportunitiesContext';
import { useOpportunitiesData } from './hooks/useOpportunitiesData';
import { useOpportunitiesState } from './hooks/useOpportunitiesState';
import { useAppliedOpportunities } from './hooks/useAppliedOpportunities';
import { useNotInterestedOpportunities } from './hooks/useNotInterestedOpportunities';
import { opportunitiesDefaultStates } from './OpportunitiesDefaultStates';
import ContinueApplicaiton from './modals/ContinueApplication/ContinueApplicaiton';
import NoNonRepresentation from './modals/NoNonRepresentation/NoNonRepresentation';
import { TModalContent } from './OpportunitiesContext.types';
import LoginRegister from './modals/LoginRegister/LoginRegister';
import OpportunitiesModal from './modals/OpportunitiesModal';
import { useNotificationModal } from '@room-match/shared-ui-components';
import { useOpportunityPublicData } from './hooks/useOpportunityPublicData';
import Share from './modals/Share/Share';
import SizeCard from 'shared/components/SizeCard/SizeCard';
import { ls } from 'shared/utils/ls';

interface Props {
  children: React.ReactNode;
}

const OpportunitiesStateProvider: React.FC<Props> = ({ children }) => {
  const { removeLS } = ls();
  const { handleSetMessage, handleOpen, NotificationModal, isOpen } = useNotificationModal({
    type: 'error',
  });
  const [opportunities, setOpportunities] = useState(opportunitiesDefaultStates.opportunities);
  const [notInterestedDataState, setNotInterestedDataState] = useState(opportunitiesDefaultStates.opportunities);
  const [appliedDataState, setAppliedDataState] = useState(opportunitiesDefaultStates.opportunities);
  const [modalProps, setModalProps] = useState<{
    content: React.ReactNode | null;
    open: boolean;
  }>({
    content: null,
    open: false,
  });
  const [showSizeModal, setShowSizeModal] = useState(false);

  const handleModalClose = useCallback(() => {
    removeLS('pending_job_application');
    setModalProps({ content: null, open: false });
  }, [removeLS]);

  const opportunitiesStates = useOpportunitiesState();
  const appliedData = useAppliedOpportunities(setAppliedDataState, appliedDataState);
  const notInterestedData = useNotInterestedOpportunities(
    setNotInterestedDataState,
    notInterestedDataState,
    setOpportunities,
  );

  const openOpportunityModal = useCallback(
    (content: TModalContent, url?: string, projectName?: string, roleName?: string) => {
      const modalContent = () => {
        switch (content) {
          case 'login_register':
            return <LoginRegister close={handleModalClose} />;
          case 'continue_application':
            return <ContinueApplicaiton close={handleModalClose} />;
          case 'no_non_representation':
            handleSetMessage(
              'No Freelance profile found!',
              'Please create a freelance profile to apply for this role',
              'OK',
            );
            return handleOpen();
          case 'already_submitted':
            handleSetMessage('Already Submitted!', 'You have already submitted your application for this role', 'OK');
            return handleOpen();
          case 'share':
            return (
              <Share
                url={url as string}
                close={handleModalClose}
                projectName={projectName as string}
                roleName={roleName as string}
              />
            );
          case 'size_card':
            return setShowSizeModal(true);

          default:
            return null;
        }
      };

      setModalProps({ content: modalContent(), open: content === 'no_non_representation' ? false : true });
    },
    [handleModalClose, handleOpen, handleSetMessage, setShowSizeModal],
  );

  const opportunitiesData = useOpportunitiesData(
    setOpportunities,
    opportunities,
    setNotInterestedDataState,
    setAppliedDataState,
    openOpportunityModal,
  );

  const projectRoles = useOpportunityPublicData();

  const proceedApplication = useCallback(
    (declined: boolean) => {
      setModalProps({ content: null, open: false });
      setShowSizeModal(false);
      opportunitiesData.continueApplication(declined);
    },
    [opportunitiesData],
  );

  return (
    <OpportunitiesContext.Provider
      value={{
        openOpportunityModal,
        notInterestedDataState,
        opportunities,
        appliedDataState,
        ...opportunitiesData,
        ...opportunitiesStates,
        ...appliedData,
        ...notInterestedData,
        ...projectRoles,
      }}
    >
      {modalProps.open && (
        <OpportunitiesModal close={handleModalClose} open={modalProps.open}>
          {modalProps.content}
        </OpportunitiesModal>
      )}
      {isOpen ? <NotificationModal /> : null}
      {/* separated size modal to call opportunitiesData.continue application */}
      {children}
      {showSizeModal && (
        <OpportunitiesModal close={() => proceedApplication(true)} open={showSizeModal}>
          <div style={{ padding: 20 }}>
            <SizeCard checkinSelfTapes onSave={() => proceedApplication(false)} isOpportunities />
          </div>
        </OpportunitiesModal>
      )}
    </OpportunitiesContext.Provider>
  );
};

export default OpportunitiesStateProvider;
